import { has, sortBy, values } from 'lodash';
import { createSelector } from 'reselect';

import { select } from 'typed-redux-saga/macro';

import {
  BoundingPoly,
  ClusterItemGroup,
  ClusterSegment,
  UDRsPolyAsset,
  UDRsPolyAssetGroupSeriesItem,
} from '@common-modules/mediaDetails/models';
import { NonEmptyArray, notNil } from '@utils';

import { selectConfig } from './config';
import {
  selectDetectionGapsAuto,
  selectDetectionGapsManual,
} from './detectionGaps';
import { selectTDO } from './tdo';
import { selectUdrAsset } from './udrs';
import {
  selectAutoInterpolationMax,
  selectManualInterpolationMax,
  selectStore,
} from './view';
import { WorkerState, GroupMapItem, SubsegmentItem } from '../models';
import { arrayHasContents } from '@common/shared/util';
import { SEGMENT_TYPE } from '@helpers/constants';
// import { selectFeatureFlags } from '@common/user-permissions'; // this selector doesn't work correctly when called from web-worker!!!

// use groupMap, subsegmentMap, objectMap to organize BoundingPolys in a nested data structure
export interface DetectionGroups {
  [groupId: string]: Omit<GroupMapItem, 'subsegmentIds'> & {
    subsegments: {
      [subsegmentId: string]: Omit<SubsegmentItem, 'objects'> & {
        objects: BoundingPoly[];
      };
    };
  };
}

export const selectClusterMap = createSelector(
  selectStore,
  (s) => s.clusterMap
);

export const selectGroupMap = createSelector(selectStore, (s) => s.groupMap);

export const selectSubsegmentsMap = createSelector(
  selectStore,
  (s) => s.subsegmentMap
);

export const selectObjectMap = createSelector(selectStore, (s) => s.objectMap);

export const selectDetectionGroupsWithDeleted = createSelector(
  selectGroupMap,
  selectSubsegmentsMap,
  selectObjectMap,
  // selectFeatureFlags,
  (groupMap, subsegmentMap, objectMap) =>
    selectDetectionGroupsHelper(
      Object.keys(groupMap),
      groupMap,
      subsegmentMap,
      objectMap,
      // featureFlags,
      true
    )
);

export const selectDetectionGroups = (groupIds?: Array<string>) =>
  createSelector(
    selectGroupMap,
    selectSubsegmentsMap,
    selectObjectMap,
    // selectFeatureFlags,
    (groupMap, subsegmentMap, objectMap) =>
      selectDetectionGroupsHelper(
        groupIds || Object.keys(groupMap),
        groupMap,
        subsegmentMap,
        objectMap
        // featureFlags
      )
  );

export const selectDetectionGroupsHelper = (
  groupIds: Array<string>,
  groupMap: WorkerState['groupMap'],
  subsegmentMap: WorkerState['subsegmentMap'],
  objectMap: WorkerState['objectMap'],
  // featureFlags: Window['config']['featureFlags'],
  // featureFlags: WorkerState['featureFlags'],
  includeDeleted = false
) =>
  groupIds.reduce((groups: DetectionGroups, groupId) => {
    const group = groupMap[groupId];
    if (!group) {
      return groups;
    }

    // filter optional detection types here instead of main thread when excluding deleted
    // if (!includeDeleted && featureFlags) {
    // if has data ignore flag VE-17384
    // if (!featureFlags.detectCards && group.type === 'card') {
    //   return groups;
    // }
    // if (!featureFlags.detectNotepads && group.type === 'notepad') {
    //   return groups;
    // }
    // }

    const groupSubsegmentIds = groupMap[groupId]?.subsegmentIds || [];

    const subsegments: DetectionGroups['groupId']['subsegments'] = {};

    groupSubsegmentIds.forEach((subsegmentId) => {
      const subsegment = subsegmentMap[subsegmentId];
      const subsegmentObjectsIds = subsegment?.objectIds || [];
      const objects: BoundingPoly[] = [];

      subsegmentObjectsIds.forEach((objectId) => {
        const object = objectMap[objectId];
        if (object && (!object.object.isDeleted || includeDeleted)) {
          objects.push(object);
        }
      });

      if (objects.length > 0 && subsegment) {
        subsegments[subsegmentId] = {
          ...subsegment,
          objects,
        };
      }
    });

    if (Object.keys(subsegments).length === 0 || !group) {
      return groups;
    } else {
      groups[groupId] = {
        ...group,
        subsegments,
      };
      return groups;
    }
  }, {});

export function* selectDetectionGroupsSubset(groupIds: Array<string>) {
  const groupMap = yield* select(selectGroupMap);
  const subsegmentMap = yield* select(selectSubsegmentsMap);
  const objectMap = yield* select(selectObjectMap);
  // const featureFlags = yield* select(selectFeatureFlags);
  return selectDetectionGroupsHelper(
    groupIds,
    groupMap,
    subsegmentMap,
    objectMap
    // featureFlags
  );
}

export const selectLastDetectionGroupsWithUpdatedClusterAssignment =
  createSelector(
    selectStore,
    (s) => s.lastDetectionGroupsWithUpdatedClusterAssignment
  );

export const selectLastAssignedClusterId = createSelector(
  selectStore,
  (s) => s.lastAssignedClusterId
);

// Checks if this item was created as a result of editing manual interpolation
export function isSubsegmentManualInterpolation(
  ss: {
    objects: {
      startTimeMs: number;
      stopTimeMs: number;
      object: { overlayObjectType: string };
    }[];
  },
  autoInterpolationMax: number,
  manualInterpolationMax: number
): boolean {
  const obj = ss.objects[0];
  if (!obj || obj.object.overlayObjectType !== 'interpolation') {
    return false;
  }
  const len = obj.stopTimeMs - obj.startTimeMs;

  return len > autoInterpolationMax && len < manualInterpolationMax;
}

export function getUDRGroupIntervalBasedSegments(
  udrGroupSeries: ReadonlyArray<UDRsPolyAssetGroupSeriesItem>,
  groupId: string
) {
  const segments = [];

  let objectIds: string[] = [];
  let groupStartTimeMs = udrGroupSeries[0] ? udrGroupSeries[0].startTimeMs : 0;

  for (let i = 1; i <= udrGroupSeries.length; i++) {
    const currUDR = udrGroupSeries[i]!; // Safe due to for loop condition
    const prevUDR = udrGroupSeries[i - 1]!; // Safe due to for loop condition

    objectIds.push(prevUDR.id);

    if (!currUDR || currUDR.startTimeMs - prevUDR.stopTimeMs > 1) {
      segments.push({
        id: objectIds[0]!, // Safe due to previous push to subsegmentIds
        groupId,
        type: 'udr' as SEGMENT_TYPE,
        numAutoInterpolations: 0,
        isManualInterpolation: false,
        subsegmentIds: objectIds, // GTODO this is same as objectIds consider combining
        objectIds, // TODO clean up saving duplicate info here and subsegmentIds
        startTimeMs: groupStartTimeMs,
        stopTimeMs: prevUDR.stopTimeMs,
      });

      groupStartTimeMs = currUDR?.startTimeMs;
      objectIds = [];
    }
  }

  return segments;
}

/**
 * Select detection cluster items groups for right-side panel.
 */
export function selectDetectionClusterGroupsHelper(
  groups: DetectionGroups,
  gapsAuto: WorkerState['detectionGaps'],
  gapsManual: WorkerState['detectionGaps'],
  manualInterpolationMax: number,
  autoInterpolationMax: number
): { [key: string]: ClusterItemGroup } {
  const clusterGroups: { [key: string]: ClusterItemGroup } = {};

  values(groups)
    .filter(notNil)
    .forEach((group) => {
      const gapsAutoByGroup = Object.values(gapsAuto[group.id] || {}) || [];
      const gapsManualByGroup = Object.values(gapsManual[group.id] || {}) || [];

      // convert all subsegments/gaps to cluster segments - and sort
      const subSegments = sortBy(
        [
          ...values(group.subsegments).map((subsegment) => {
            const gapsAutoBySegment = gapsAutoByGroup.filter(
              ({ subsegmentId }) => subsegmentId === subsegment.id
            );
            return {
              id: subsegment.id,
              groupId: group.id,
              type: group.type,
              startTimeMs: subsegment.startTimeMs,
              stopTimeMs: subsegment.stopTimeMs,
              numAutoInterpolations: gapsAutoBySegment.length,
              isManualInterpolation:
                isSubsegmentManualInterpolation(
                  subsegment,
                  autoInterpolationMax,
                  manualInterpolationMax
                ) || false,
              subsegmentIds: [subsegment.id],
              objectIds: subsegment.objects.map(({ id }) => id),
            };
          }),
          ...gapsAutoByGroup.map<ClusterSegment>((gap) => ({
            id: gap.subsegmentId,
            groupId: group.id,
            type: group.type,
            startTimeMs: gap.leftObject.stopTimeMs,
            stopTimeMs: gap.rightObject.startTimeMs,
            numAutoInterpolations: 1,
            isManualInterpolation: false,
            subsegmentIds: [gap.subsegmentId],
            objectIds: [gap.id],
          })),
          ...gapsManualByGroup.map<ClusterSegment>((gap) => ({
            id: gap.subsegmentId,
            groupId: group.id,
            type: group.type,
            startTimeMs: gap.leftObject.stopTimeMs,
            stopTimeMs: gap.rightObject.startTimeMs,
            numAutoInterpolations: 0,
            isManualInterpolation: true,
            subsegmentIds: [gap.subsegmentId],
            objectIds: [gap.id],
          })),
        ],
        'startTimeMs'
      );

      // ----- produce final segments (combine some of the segments) - these are visual rows than expand under each ClusterItem in the cluster panel

      // let t0 = new Date().getTime();
      const segments: ClusterSegment[] = [];

      const firstSubSeg = subSegments?.[0];
      if (!firstSubSeg) {
        return;
      }
      let segment = firstSubSeg;
      let objectIds: string[] = [];
      let subsegmentIds: string[] = [];
      let numAutoInterpolations = 0;
      let stopTimeMs = firstSubSeg.stopTimeMs;

      for (const subSeg of subSegments) {
        // finalize segment: when discontinuity, active subsegment or current detection is manual interpolation
        if (
          subSeg.startTimeMs - stopTimeMs > 1 ||
          segment.isManualInterpolation ||
          subSeg.isManualInterpolation
        ) {
          segments.push({
            ...segment,
            stopTimeMs,
            numAutoInterpolations,
            subsegmentIds,
            objectIds,
          });

          // initialize next segment
          segment = subSeg;
          subsegmentIds = [];
          objectIds = [];
          numAutoInterpolations = 0;
        }

        // update segment
        subsegmentIds.push(subSeg.id);
        objectIds.push(...subSeg.objectIds);
        stopTimeMs = subSeg.stopTimeMs;
        numAutoInterpolations += subSeg.numAutoInterpolations;
      }

      // append final segment
      segments.push({
        ...segment,
        stopTimeMs,
        numAutoInterpolations,
        subsegmentIds,
        objectIds,
      });

      // previous version was very slow
      // let t1 = new Date().getTime();
      // console.log('Duration: [clusters.ts] combine subsegments:', (t1 - t0) / 1000);

      const clusterGroup: ClusterItemGroup = {
        id: group.id,
        clusterId: group.clusterId || group.id,
        userLabel: group.userLabel,
        picUri: group.picUri,
        segments: segments as NonEmptyArray<ClusterSegment>, // Safe due to previous push
        type: group.type,
        redactionCode: group.redactionCode,
        redactionConfig: group.redactionConfig,
        version: group.version,
      };

      clusterGroups[group.id] = clusterGroup; // key by groupId
    });
  return clusterGroups;
}

export const selectDetectionClusterGroupsSubset = (groupIds: Array<string>) =>
  createSelector(
    selectDetectionGroups(groupIds),
    selectDetectionGapsAuto(groupIds),
    selectDetectionGapsManual(groupIds),
    selectManualInterpolationMax,
    selectAutoInterpolationMax,
    (
      groups,
      gapsAuto,
      gapsManual,
      manualInterpolationMax,
      autoInterpolationMax
    ) =>
      selectDetectionClusterGroupsHelper(
        groups,
        gapsAuto,
        gapsManual,
        manualInterpolationMax,
        autoInterpolationMax
      )
  );

export const selectDetectionClusterGroups = createSelector(
  selectDetectionGroups(),
  selectDetectionGapsAuto(),
  selectDetectionGapsManual(),
  selectManualInterpolationMax,
  selectAutoInterpolationMax,
  (
    groups,
    gapsAuto,
    gapsManual,
    manualInterpolationMax,
    autoInterpolationMax
  ) =>
    selectDetectionClusterGroupsHelper(
      groups,
      gapsAuto,
      gapsManual,
      manualInterpolationMax,
      autoInterpolationMax
    )
);

/**
 * Select udr cluster item groups for right-side panel.
 */
function selectUdrClusterGroupsHelper(
  groupsIds: string[],
  udrs: UDRsPolyAsset,
  tdoId: string | undefined,
  apiRoot: string | undefined
): { [key: string]: ClusterItemGroup } {
  const clusterGroups: { [groupId: string]: ClusterItemGroup } = {};

  if (has(udrs, 'boundingPolys')) {
    groupsIds.forEach((clusterId) => {
      const udr = udrs.boundingPolys[clusterId];
      if (udr) {
        const segments = getUDRGroupIntervalBasedSegments(
          udr.series,
          udr.groupId
        );
        if (arrayHasContents(segments)) {
          let timeOffset = udr.series[0]?.startTimeMs || 0;
          if (timeOffset > 0 && udr.series[0]) {
            // trick to get thumbnail from image media - todo find better solution (see if can exact from image instead of using media-streamer!)
            timeOffset =
              (udr.series[0].startTimeMs + udr.series[0].stopTimeMs) / 2;
          }

          const group: ClusterItemGroup = {
            id: udr.groupId,
            clusterId: udr.clusterId || udr.groupId,
            userLabel: udr.userLabel,
            type: 'udr',
            // https://api.stage.veritone.com/media-streamer/image/:tdoId?:offsetMs=0&x[0]=0&y[0]=0&x[1]=0&y[1]=0&x[2]=0&y[2]=0&x[3]=0&y[3]=0
            picUri:
              tdoId && udr.series[0] && apiRoot
                ? `${apiRoot}/media-streamer/image/${tdoId}?offsetMs=${timeOffset.toString()}&` +
                  udr.series[0].object.boundingPoly
                    .map(({ x, y }, j) => `x[${j}]=${x}&y[${j}]=${y}`)
                    .join('&')
                : '',
            segments,
            redactionCode: udr.redactionCode,
            redactionConfig: udr.redactionConfig,
            textOverlay: udr.textOverlay,
            version: udr.version,
          };

          clusterGroups[group.id] = group;
        }
      }
    });
  }
  return clusterGroups;
}

export const selectUdrClusterGroups = createSelector(
  selectUdrAsset,
  selectTDO,
  selectConfig,
  (udrs, tdo, config) =>
    selectUdrClusterGroupsHelper(
      Object.keys(udrs?.boundingPolys || {}),
      udrs,
      tdo?.id,
      config?.apiRoot
    )
);

/* old version not actually a selector
export function* selectUdrClusterGroupsSubset(groupIds: Array<string>) {
  const udrs = yield* select(selectUdrAsset);
  const tdo = yield* select(selectTDO);
  const config = yield* select(selectConfig);
  return selectUdrClusterGroupsHelper(
    groupIds,
    udrs,
    tdo?.id,
    config?.apiRoot
  );
}
  */

export const selectUdrClusterGroupsSubset = (groupIds: Array<string>) =>
  createSelector(selectUdrAsset, selectTDO, selectConfig, (udrs, tdo, config) =>
    selectUdrClusterGroupsHelper(groupIds, udrs, tdo?.id, config?.apiRoot)
  );
